<script setup lang="ts">
import { nonDisplayedItemApi } from '@/api/entities/review/nonDisplayedItem'
import type { NonDisplayedItem } from '@/api/entities/review/nonDisplayedItem'
import { useClientStore } from '@/stores/client'

definePage({
  meta: {
    title: '진열되지 않은 상품',
    action: 'read',
    subject: 'DriverConfig',
  },
})

const { snackbar, handleError } = useErrorHandler()
const clientStore = useClientStore()
const selectedClient = computed(() => clientStore.selectedClient)

// State
const loading = ref(false)
const items = ref<NonDisplayedItem[]>([])
const totalCount = ref(0)
const page = ref(1)
const itemsPerPage = ref(25)
const sortBy = ref<{ key: string; order?: 'asc' | 'desc' }[]>([{ key: 'detected_at', order: 'desc' }])
const selectedItems = ref<NonDisplayedItem[]>([])
const deletingMappings = ref(false)

// Filters
const search = ref('')
const statusFilter = ref<string | null>(null)
const driverCodeFilter = ref<string | null>(null)

// Confirm dialog
const confirmDialog = ref(false)
const confirmMessage = ref('')
const confirmAction = ref<(() => void | Promise<void>) | null>(null)
const isConfirmExecuting = ref(false)

const headers = [
  { title: '설정명', key: 'config_name', sortable: true },
  { title: '설정 ID', key: 'config_id', sortable: false, width: '90px' },
  { title: '드라이버', key: 'driver_code', sortable: false, width: '160px' },
  { title: '상품 코드', key: 'target_item_code', sortable: true },
  { title: '감지 시간', key: 'detected_at', sortable: true, width: '170px' },
  { title: '상태', key: 'resolved_at', sortable: true, width: '100px' },
  { title: '액션', key: 'actions', sortable: false, width: '80px' },
]

const statusOptions = [
  { title: '전체', value: null },
  { title: '미해결', value: 'unresolved' },
  { title: '해결됨', value: 'resolved' },
]

const driverCodeOptions = [
  { title: '전체', value: null },
  { title: '브랜드스토어', value: 'naver_brand_store' },
  { title: '스마트스토어', value: 'naver_smart_store' },
]

const loadItems = async () => {
  if (!selectedClient.value?.id) {
    items.value = []
    totalCount.value = 0
    
    return
  }

  loading.value = true
  try {
    const sortKey = sortBy.value[0]?.key || 'detected_at'
    const sortOrder = sortBy.value[0]?.order || 'desc'

    const params: Record<string, string | number> = {
      page: page.value,
      per_page: itemsPerPage.value,
      sort: sortKey,
      order: sortOrder,
    }

    if (search.value) params.search = search.value
    if (statusFilter.value) params.status = statusFilter.value
    if (driverCodeFilter.value) params.driver_code = driverCodeFilter.value

    const { success, data } = await nonDisplayedItemApi.list(selectedClient.value.id, params)

    if (success && data) {
      items.value = data.data || []
      totalCount.value = data.total || 0
    }
    else {
      snackbar.error('데이터 로드 실패')
    }
  }
  catch (error) {
    handleError(error, { notificationMessage: '진열되지 않은 상품 목록 로드 실패' })
  }
  finally {
    loading.value = false
  }
}

const showConfirm = (message: string, action: () => void | Promise<void>) => {
  confirmMessage.value = message
  confirmAction.value = action
  confirmDialog.value = true
}

const executeConfirm = async () => {
  if (isConfirmExecuting.value || !confirmAction.value) return
  isConfirmExecuting.value = true
  try {
    await confirmAction.value()
  }
  finally {
    isConfirmExecuting.value = false
    confirmDialog.value = false
  }
}

const deleteSelectedMappings = () => {
  if (selectedItems.value.length === 0) return

  const unresolvedCount = selectedItems.value.filter(i => !i.resolved_at).length
  if (unresolvedCount === 0) {
    snackbar.warning('이미 모두 해결된 항목입니다.')
    
    return
  }

  showConfirm(
    `선택된 ${unresolvedCount}개 상품의 매핑을 삭제하시겠습니까? (review_target_item_map에서 삭제됩니다)`,
    async () => {
      if (deletingMappings.value) return
      deletingMappings.value = true
      try {
        const ids = selectedItems.value.filter(i => !i.resolved_at).map(i => i.id)
        const { success, data } = await nonDisplayedItemApi.deleteMappings(selectedClient.value!.id, ids)
        if (success && data) {
          snackbar.success(`매핑 ${data.deleted_mappings}건 삭제, ${data.resolved_items}건 해결 처리 완료`)
          selectedItems.value = []
          await loadItems()
        }
        else {
          snackbar.error('매핑 삭제 실패')
        }
      }
      catch (error) {
        handleError(error, { notificationMessage: '매핑 삭제 실패' })
      }
      finally {
        deletingMappings.value = false
      }
    },
  )
}

const deleteSingleMapping = (item: NonDisplayedItem) => {
  if (item.resolved_at) {
    snackbar.warning('이미 해결된 항목입니다.')
    
    return
  }
  showConfirm(
    `${item.target_item_code} 매핑을 삭제하시겠습니까?`,
    async () => {
      try {
        const { success, data } = await nonDisplayedItemApi.deleteMappings(selectedClient.value!.id, [item.id])
        if (success && data) {
          snackbar.success('매핑 삭제 완료')
          await loadItems()
        }
        else {
          snackbar.error('매핑 삭제 실패')
        }
      }
      catch (error) {
        handleError(error, { notificationMessage: '매핑 삭제 실패' })
      }
    },
  )
}

const deleteResolvedItems = () => {
  showConfirm(
    '해결된 항목을 모두 정리(삭제)하시겠습니까?',
    async () => {
      try {
        const { success, data } = await nonDisplayedItemApi.deleteResolved(selectedClient.value!.id)
        if (success && data) {
          snackbar.success(`${data.deleted}건 정리 완료`)
          await loadItems()
        }
        else {
          snackbar.error('정리 실패')
        }
      }
      catch (error) {
        handleError(error, { notificationMessage: '해결된 항목 정리 실패' })
      }
    },
  )
}

const formatDate = (dateStr: string | null) => {
  if (!dateStr) return '-'
  
  return new Date(dateStr).toLocaleString('ko-KR', { timeZone: 'Asia/Seoul' })
}

const driverCodeLabel = (code: string) => {
  const map: Record<string, string> = {
    naver_brand_store: '브랜드스토어',
    naver_smart_store: '스마트스토어',
  }

  
  return map[code] || code
}

// Watchers
watch(selectedClient, () => {
  page.value = 1
  selectedItems.value = []
  loadItems()
}, { immediate: true })

watch([statusFilter, driverCodeFilter], () => {
  page.value = 1
  loadItems()
})

let searchTimeout: ReturnType<typeof setTimeout> | null = null
watch(search, () => {
  if (searchTimeout) clearTimeout(searchTimeout)
  searchTimeout = setTimeout(() => {
    page.value = 1
    loadItems()
  }, 500)
})

onBeforeUnmount(() => {
  if (searchTimeout) clearTimeout(searchTimeout)
})
</script>

<template>
  <VRow>
    <VCol cols="12">
      <VCard>
        <VCardText>
          <VRow class="mb-4">
            <VCol
              cols="12"
              md="2"
            >
              <VTextField
                v-model="search"
                placeholder="상품코드/설정명 검색"
                density="compact"
                hide-details
                prepend-inner-icon="tabler-search"
                clearable
              />
            </VCol>

            <VCol
              cols="12"
              md="2"
            >
              <VSelect
                v-model="statusFilter"
                :items="statusOptions"
                item-title="title"
                item-value="value"
                label="상태"
                density="compact"
                hide-details
              />
            </VCol>

            <VCol
              cols="12"
              md="2"
            >
              <VSelect
                v-model="driverCodeFilter"
                :items="driverCodeOptions"
                item-title="title"
                item-value="value"
                label="드라이버"
                density="compact"
                hide-details
              />
            </VCol>

            <VCol
              cols="12"
              md="6"
            >
              <div class="d-flex flex-wrap gap-2">
                <VBtn
                  color="error"
                  variant="outlined"
                  :disabled="selectedItems.length === 0 || !selectedClient || deletingMappings"
                  :loading="deletingMappings"
                  @click="deleteSelectedMappings"
                >
                  선택 매핑 삭제 ({{ selectedItems.filter(i => !i.resolved_at).length }})
                </VBtn>
                <VBtn
                  color="warning"
                  variant="outlined"
                  :disabled="!selectedClient"
                  @click="deleteResolvedItems"
                >
                  해결됨 정리
                </VBtn>
                <VBtn
                  color="primary"
                  :loading="loading"
                  :disabled="!selectedClient"
                  @click="loadItems"
                >
                  새로고침
                </VBtn>
              </div>
            </VCol>

            <VCol
              cols="12"
              class="text-right"
            >
              <span class="text-body-2">
                총 {{ totalCount }}개
              </span>
            </VCol>
          </VRow>

          <VDataTableServer
            v-model="selectedItems"
            v-model:items-per-page="itemsPerPage"
            v-model:page="page"
            v-model:sort-by="sortBy"
            :headers="headers"
            :items="items"
            :items-length="totalCount"
            :loading="loading"
            show-select
            item-value="id"
            return-object
            density="compact"
            class="text-no-wrap"
            @update:page="loadItems"
            @update:sort-by="loadItems"
            @update:items-per-page="loadItems"
          >
            <template #item.driver_code="{ item }">
              <VChip
                size="small"
                :color="item.driver_code === 'naver_brand_store' ? 'primary' : 'info'"
              >
                {{ driverCodeLabel(item.driver_code) }}
              </VChip>
            </template>

            <template #item.detected_at="{ item }">
              {{ formatDate(item.detected_at) }}
            </template>

            <template #item.resolved_at="{ item }">
              <VChip
                size="small"
                :color="item.resolved_at ? 'success' : 'error'"
              >
                {{ item.resolved_at ? '해결됨' : '미해결' }}
              </VChip>
            </template>

            <template #item.actions="{ item }">
              <VBtn
                icon
                variant="text"
                size="small"
                color="error"
                :disabled="!!item.resolved_at"
                @click="deleteSingleMapping(item)"
              >
                <VIcon icon="tabler-trash" />
                <VTooltip activator="parent">
                  매핑 삭제
                </VTooltip>
              </VBtn>
            </template>
          </VDataTableServer>
        </VCardText>
      </VCard>
    </VCol>
  </VRow>

  <!-- Confirmation Dialog -->
  <VDialog
    v-model="confirmDialog"
    persistent
    max-width="450"
  >
    <VCard>
      <VCardTitle>확인</VCardTitle>
      <VCardText>{{ confirmMessage }}</VCardText>
      <VCardActions>
        <VSpacer />
        <VBtn
          variant="outlined"
          @click="confirmDialog = false"
        >
          취소
        </VBtn>
        <VBtn
          color="error"
          :loading="isConfirmExecuting"
          :disabled="isConfirmExecuting"
          @click="executeConfirm"
        >
          확인
        </VBtn>
      </VCardActions>
    </VCard>
  </VDialog>
</template>
